import { useCallback } from 'react'

import { IViewComponent } from '@/core/app-builder/app/component'
import { useBuilderAccordionContext } from '@/context/__project__/BuilderAccordionContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { ComponentPanelTab, LeadingSidebarTab } from '@/shared/builder-ui'

interface NavigationOptions {
  component: IViewComponent
  sidebarTab?: LeadingSidebarTab
  componentTab?: ComponentPanelTab
  accordionCategory?: 'design' | 'data' | 'action' | 'behavior'
  accordionKey?: string
  resetActiveComponent?: boolean
}

export function useComponentNavigation() {
  const { setActiveTab, setActiveComponent, setActiveComponentTab, setActiveComponentAccordion, activeLayer } =
    useAppBuilderContext()

  const { setAccordions } = useBuilderAccordionContext()

  const navigateToComponent = useCallback(
    (options: NavigationOptions) => {
      const {
        component,
        sidebarTab = LeadingSidebarTab.Component,
        componentTab,
        accordionCategory,
        accordionKey,
        resetActiveComponent = false,
      } = options

      // 1. Set sidebar tab
      setActiveTab(sidebarTab)

      // 2. Reset active component if needed (for tab Component)
      if (resetActiveComponent && sidebarTab === LeadingSidebarTab.Component) {
        setActiveComponent(undefined)
      }

      // 3. Set component tab if provided
      if (componentTab) {
        setActiveComponentTab(componentTab)
      }

      // 4. Set accordion state if provided
      if (accordionCategory && accordionKey && component._id) {
        setAccordions(accordionCategory, component._id, accordionKey)
        setActiveComponentAccordion(accordionKey)
      }

      // 5. Set active component
      setActiveComponent({
        layerType: activeLayer.type,
        compId: component._ComponentID || component._GroupID,
        id: component._id,
        groupId: component._GroupID,
      })
    },
    [setActiveTab, setActiveComponent, setActiveComponentTab, setActiveComponentAccordion, setAccordions, activeLayer]
  )

  // Helper functions for general cases
  const openComponentDataSettings = useCallback(
    (component: IViewComponent, accordionKey?: string) => {
      navigateToComponent({
        component,
        componentTab: ComponentPanelTab.Data,
        accordionCategory: 'data',
        accordionKey,
      })
    },
    [navigateToComponent]
  )

  const openComponentDesignSettings = useCallback(
    (component: IViewComponent, accordionKey?: string) => {
      navigateToComponent({
        component,
        componentTab: ComponentPanelTab.Design,
        accordionCategory: 'design',
        accordionKey,
      })
    },
    [navigateToComponent]
  )

  const openComponentActionSettings = useCallback(
    (component: IViewComponent, accordionKey?: string) => {
      navigateToComponent({
        component,
        componentTab: ComponentPanelTab.Action,
        accordionCategory: 'action',
        accordionKey,
      })
    },
    [navigateToComponent]
  )

  const openComponentBehaviorSettings = useCallback(
    (component: IViewComponent, accordionKey?: string) => {
      navigateToComponent({
        component,
        componentTab: ComponentPanelTab.Behavior,
        accordionCategory: 'behavior',
        accordionKey,
      })
    },
    [navigateToComponent]
  )

  return {
    navigateToComponent,
    openComponentDataSettings,
    openComponentDesignSettings,
    openComponentActionSettings,
    openComponentBehaviorSettings,
  }
}
